import React, { useEffect, useContext, useState, useRef } from 'react';
import { AppContext } from '../../../context/Context';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import {
  Camera as CameraIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material/';
import { SocketContext } from '../../../context/SocketContext';
import toast from 'react-hot-toast';

function ModalUserExpired() {
  const { t } = useTranslation();
  const [confirmationStep, setConfirmationStep] = useState(0);

  const { socket } = useContext(SocketContext);

  const {
    session,
    updateIsLoadingScreen,
    updateIsFullUserEditing,
    updateShowModalIsVerificationNeeded,
    updateFocusOnMainScreen,
    logoutUser,
    updateShowModalUserExpired,
    updateIsLimitSurpassed,
    updateIsDailyLimitSurpassed,
    updateIsMonthlyLimitSurpassed,
    updateShowUserInfo,
    updatePrint,
    resetTransactions,
    updateReceiptToPrint,
    updateIsCard,
    updateIsCash,
    updatePayCard,
    updatePayCash,
    updatePaySpillerkonto,
    updateChange,
    updatePaymentType,
    updateIsPaymentOpen,
    updateIsSellerOpen,
    updateCurrentUser,
    updateCurrentUserID,
    updateUserLogin,
    updateUserInfo,
    updateIsFastUserLogin,
    updateIsAnonUser,
    updateIsHybridUser,
    updateIsFullUser,
    updateUserParent,
    updateUserChildren,
    updateUserSiblings,
    resetTicketsCH,
    updateTotal,
    updateIsVerificationNeeded,
    hall,
    parentInfo,
    userInfo,
    updateIsFullUserRegistration,
    updateIsNeedConfirmation,
    updateIsEditingAfterExpiry,
  } = useContext(AppContext);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);
  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    const handleUpdateUserLastTimeChecked = (data) => {
      if (!data) {
        toast.error(t('error.error'));

        updateIsLoadingScreen(false);
        return;
      }

      updateIsEditingAfterExpiry(false);
      var data = {
        auth_value: userDetailsToCheck.username,
        isExact: true,
        searchByUsername: true,
        action: 'searchMember',
        session: sessionRef.current,
      };
      socket.emit('searchMember', data);
      updateShowModalUserExpired(false);
      updateFocusOnMainScreen(true);
    };

    socket.on('updateUserLastTimeChecked', handleUpdateUserLastTimeChecked);

    return () => {
      socket.off('updateUserLastTimeChecked', handleUpdateUserLastTimeChecked);
    };
  }, [socket]);

  const handleCancel = async () => {
    updateFocusOnMainScreen(true);
    updateIsNeedConfirmation(false);

    updateIsLimitSurpassed(false);
    updateIsDailyLimitSurpassed(false);
    updateIsMonthlyLimitSurpassed(false);

    logoutUser();
    updateShowUserInfo(false);
    updatePrint(false);
    resetTransactions();
    updateReceiptToPrint(false);
    updateIsCard(false);
    updateIsCash(false);
    updateTotal(0);
    updatePayCard(0);
    updatePayCash(0);
    updatePaySpillerkonto(0);
    updateChange(0);
    updateCurrentUser('');
    updateCurrentUserID('');
    updateUserLogin(false);
    updatePaymentType('');
    updateIsPaymentOpen(false);
    updateIsSellerOpen(false);
    logoutUser();
    updateCurrentUser('');
    updateCurrentUserID('');
    updateUserLogin(false);
    updateUserInfo({});
    updateIsFastUserLogin(false);
    updateIsAnonUser(false);
    updateIsHybridUser(false);
    updateIsFullUser(false);
    updateUserParent(false);
    updateUserChildren([]);
    updateUserSiblings([]);
    resetTicketsCH();
    updateIsVerificationNeeded(false);
    updateShowModalIsVerificationNeeded(false);
    updateFocusOnMainScreen(true);
    updateIsFullUserEditing(false);
    updateShowModalUserExpired(false);
  };

  const handleConfirm = async () => {
    updateShowModalUserExpired(false);
    updateIsNeedConfirmation(true);
    updateIsEditingAfterExpiry(true);
    updateIsFullUserEditing(true);
    updateFocusOnMainScreen(false);
  };

  const userDetailsToCheck = parentInfo?.id ? parentInfo : userInfo;

  const normalizePepValue = (value) => {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase();

      if (
        normalizedValue === 'yes' ||
        normalizedValue === 'ja' ||
        normalizedValue === 'true' ||
        normalizedValue === '1'
      ) {
        return true;
      }

      if (
        normalizedValue === 'no' ||
        normalizedValue === 'nei' ||
        normalizedValue === 'false' ||
        normalizedValue === '0'
      ) {
        return false;
      }
    }

    return false;
  };

  const isPepUser = normalizePepValue(
    userDetailsToCheck?.['IS PEP'] ??
      userDetailsToCheck?.['Is PEP'] ??
      userDetailsToCheck?.isPep ??
      userDetailsToCheck?.is_pep
  );

  const addressCountry = userDetailsToCheck?.country || '-';
  const addressPostalCode =
    userDetailsToCheck?.['Postal Code'] ||
    userDetailsToCheck?.postalCode ||
    '-';
  const addressStreet =
    userDetailsToCheck?.address ||
    userDetailsToCheck?.['Street Address'] ||
    '-';
  const mobile = userDetailsToCheck?.mobile || '-';

  const handleInitialConfirm = () => {
    setConfirmationStep(1);
  };

  const handleFirstStepYes = () => {
    setConfirmationStep(2);
  };

  const handleSecondStepYes = () => {
    setConfirmationStep(3);
  };

  const handleBackFromStep2 = () => {
    setConfirmationStep(1);
  };

  const handleBackFromStep3 = () => {
    setConfirmationStep(2);
  };

  const handleThirdStepYes = () => {
    socket.emit('updateUserLastTimeChecked', { userID: userDetailsToCheck.id });
    updateIsLoadingScreen(true);
  };

  const renderStepContent = () => {
    if (confirmationStep === 1) {
      return (
        <>
          <h1>{t('sales.expiredPepTitle')}</h1>
          <p>
            {isPepUser
              ? t('sales.expiredPepQuestionPep')
              : t('sales.expiredPepQuestionNotPep')}
          </p>
          <div className="react-confirm-alert-button-group">
            <button
              className="btn-custom-yes"
              onClick={handleFirstStepYes}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CheckCircleIcon style={{ fontSize: '1.2em' }} />
              {t('sales.yes')}
            </button>

            <button
              className="btn-custom-no"
              onClick={handleConfirm}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CancelIcon style={{ fontSize: '1.2em' }} />
              {t('sales.no')}
            </button>
          </div>
        </>
      );
    }

    if (confirmationStep === 2) {
      return (
        <>
          <h1>{t('sales.expiredAddressTitle')}</h1>
          <p>{t('sales.expiredAddressQuestion')}</p>
          <p>
            {t('sales.country')}: <strong>{addressCountry}</strong>
          </p>
          <p>
            {t('sales.postalCode')}: <strong>{addressPostalCode}</strong>
          </p>
          <p>
            {t('sales.address')}: <strong>{addressStreet}</strong>
          </p>
          <div className="react-confirm-alert-button-group">
            <button
              className="btn-custom-yes"
              onClick={handleSecondStepYes}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CheckCircleIcon style={{ fontSize: '1.2em' }} />
              {t('sales.yes')}
            </button>

            <button
              className="btn-custom-no"
              onClick={handleConfirm}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CancelIcon style={{ fontSize: '1.2em' }} />
              {t('sales.no')}
            </button>

            <button
              className="btn-custom-postpone"
              onClick={handleBackFromStep2}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <ArrowBackIcon style={{ fontSize: '1.2em' }} />
              {t('sales.expiredBack')}
            </button>
          </div>
        </>
      );
    }

    if (confirmationStep === 3) {
      return (
        <>
          <h1>{t('sales.expiredContactTitle')}</h1>
          <p>{t('sales.expiredContactQuestion')}</p>
          <p>
            {t('sales.phonenumber')}: <strong>{mobile}</strong>
          </p>
          <div className="react-confirm-alert-button-group">
            <button
              className="btn-custom-yes"
              onClick={handleThirdStepYes}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CheckCircleIcon style={{ fontSize: '1.2em' }} />
              {t('sales.yes')}
            </button>

            <button
              className="btn-custom-no"
              onClick={handleConfirm}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <CancelIcon style={{ fontSize: '1.2em' }} />
              {t('sales.no')}
            </button>

            <button
              className="btn-custom-postpone"
              onClick={handleBackFromStep3}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <ArrowBackIcon style={{ fontSize: '1.2em' }} />
              {t('sales.expiredBack')}
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <h1>{t('sales.expiredTitle')} </h1>
        <p>{t('sales.expiredText')}</p>
        <div className="react-confirm-alert-button-group">
          <button
            className="btn-custom-yes"
            onClick={handleInitialConfirm}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <CheckCircleIcon style={{ fontSize: '1.2em' }} />

            {t('sales.confirm')}
          </button>

          <button
            className="btn-custom-no"
            onClick={() => {
              handleCancel();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <CancelIcon style={{ fontSize: '1.2em' }} />

            {t('sales.cancel')}
          </button>
        </div>
      </>
    );
  };

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          <div className="react-confirm-alert-body" style={{ width: '1000px' }}>
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalUserExpired;
